import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  ChevronLeft,
  KeyRound,
  Plus,
  ShieldCheck,
  Trash2,
  UserCog,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { PasswordField } from '@/components/ui/password-field';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { useAuthStore, type Role } from '@/stores/auth';

interface Tenant {
  tenant_id: string;
  name: string;
  status: 'active' | 'disabled';
  created_at: string;
  quota_users: number;
  quota_topics_per_day: number;
}

interface AdminUser {
  user_id: string;
  username: string;
  display_name: string;
  email?: string | null;
  role: Role;
  tenant_id: string | null;
  status: 'active' | 'disabled';
  created_at: string;
  last_login_at?: string | null;
}

async function extractDetail(err: unknown): Promise<string> {
  try {
    if (err && typeof err === 'object' && 'response' in err) {
      const res = (err as { response: Response }).response;
      const body = await res.clone().json();
      return body?.detail ?? `HTTP ${res.status}`;
    }
  } catch {
    /* fall through */
  }
  return err instanceof Error ? err.message : '网络异常';
}

export function IamPage() {
  const role = useAuthStore((s) => s.user?.role);
  if (role !== 'SUPER_ADMIN') {
    return (
      <div className="space-y-6 p-6 lg:p-8">
        <div className="rounded-xl border border-danger/30 bg-danger/5 p-6 text-sm text-danger">
          需要超级管理员权限才能访问此页面。
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6 p-6 lg:p-8">
      <header>
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-brand-500"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> 返回管理员
        </Link>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">身份与租户</h1>
        <p className="mt-1 text-sm text-neutral-600">管理租户与所有用户(超管视角)</p>
      </header>
      <Tabs defaultValue="tenants">
        <TabsList>
          <TabsTrigger value="tenants">
            <Building2 className="mr-1.5 h-3.5 w-3.5" /> 租户
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="mr-1.5 h-3.5 w-3.5" /> 用户
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tenants">
          <TenantsPanel />
        </TabsContent>
        <TabsContent value="users">
          <UsersPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Tenants ----------

function TenantsPanel() {
  const qc = useQueryClient();
  const { data: tenants = [], isLoading } = useQuery<Tenant[]>({
    queryKey: ['admin', 'tenants'],
    queryFn: () => api.get('admin/tenants').json<Tenant[]>(),
  });
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-neutral-500">共 {tenants.length} 个租户</p>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> 新建租户
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-outline-variant/30 bg-surface-variant/40 text-xs text-neutral-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">ID</th>
                <th className="px-4 py-2 text-left font-medium">名称</th>
                <th className="px-4 py-2 text-left font-medium">状态</th>
                <th className="px-4 py-2 text-right font-medium">用户配额</th>
                <th className="px-4 py-2 text-right font-medium">日抓配额</th>
                <th className="px-4 py-2 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-xs text-neutral-400">
                    加载中…
                  </td>
                </tr>
              )}
              {!isLoading && tenants.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-xs text-neutral-400">
                    暂无租户
                  </td>
                </tr>
              )}
              {tenants.map((t) => (
                <TenantRow
                  key={t.tenant_id}
                  tenant={t}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <NewTenantModal
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })}
      />
    </div>
  );
}

function TenantRow({ tenant, onChanged }: { tenant: Tenant; onChanged: () => void }) {
  const toggle = useMutation({
    mutationFn: () =>
      api
        .patch(`admin/tenants/${tenant.tenant_id}`, {
          json: { status: tenant.status === 'active' ? 'disabled' : 'active' },
        })
        .json<Tenant>(),
    onSuccess: () => {
      toast.success('租户状态已更新');
      onChanged();
    },
    onError: async (e) => toast.error('更新失败', await extractDetail(e)),
  });
  return (
    <tr className="border-b border-outline-variant/15 last:border-0">
      <td className="px-4 py-2.5 font-mono text-xs text-neutral-700">{tenant.tenant_id}</td>
      <td className="px-4 py-2.5">{tenant.name}</td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${
            tenant.status === 'active'
              ? 'bg-success/10 text-success'
              : 'bg-neutral-100 text-neutral-500'
          }`}
        >
          {tenant.status === 'active' ? '启用' : '已停用'}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right text-xs text-neutral-600">{tenant.quota_users}</td>
      <td className="px-4 py-2.5 text-right text-xs text-neutral-600">
        {tenant.quota_topics_per_day}
      </td>
      <td className="px-4 py-2.5 text-right">
        <Button
          variant="ghost"
          size="sm"
          disabled={toggle.isPending || tenant.tenant_id === 'tnt_default'}
          onClick={() => toggle.mutate()}
        >
          {tenant.status === 'active' ? '停用' : '启用'}
        </Button>
      </td>
    </tr>
  );
}

function NewTenantModal({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  onCreated: () => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [quotaUsers, setQuotaUsers] = useState(20);
  const [quotaTopics, setQuotaTopics] = useState(100);
  const create = useMutation({
    mutationFn: () =>
      api
        .post('admin/tenants', {
          json: {
            tenant_id: tenantId.trim(),
            name: name.trim(),
            quota_users: quotaUsers,
            quota_topics_per_day: quotaTopics,
          },
        })
        .json<Tenant>(),
    onSuccess: () => {
      toast.success('租户已创建');
      onOpenChange(false);
      setTenantId('');
      setName('');
      setQuotaUsers(20);
      setQuotaTopics(100);
      onCreated();
    },
    onError: async (e) => toast.error('创建失败', await extractDetail(e)),
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="新建租户"
      description="租户 ID 须以 tnt_ 开头,只能含小写字母/数字/下划线"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
            创建
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="space-y-3"
      >
        <Field label="租户 ID">
          <Input
            placeholder="tnt_acme"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            pattern="^tnt_[a-z0-9_]{2,32}$"
            required
          />
        </Field>
        <Field label="显示名">
          <Input
            placeholder="ACME 影视"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="用户配额">
            <Input
              type="number"
              min={1}
              value={quotaUsers}
              onChange={(e) => setQuotaUsers(Number(e.target.value))}
            />
          </Field>
          <Field label="日抓配额">
            <Input
              type="number"
              min={1}
              value={quotaTopics}
              onChange={(e) => setQuotaTopics(Number(e.target.value))}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Users ----------

function UsersPanel() {
  const qc = useQueryClient();
  const selfId = useAuthStore((s) => s.user?.user_id);
  const { data: users = [], isLoading } = useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get('admin/users').json<AdminUser[]>(),
  });
  const { data: tenants = [] } = useQuery<Tenant[]>({
    queryKey: ['admin', 'tenants'],
    queryFn: () => api.get('admin/tenants').json<Tenant[]>(),
  });
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-neutral-500">共 {users.length} 个用户</p>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> 新建用户
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-outline-variant/30 bg-surface-variant/40 text-xs text-neutral-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">账号</th>
                <th className="px-4 py-2 text-left font-medium">显示名</th>
                <th className="px-4 py-2 text-left font-medium">角色</th>
                <th className="px-4 py-2 text-left font-medium">租户</th>
                <th className="px-4 py-2 text-left font-medium">状态</th>
                <th className="px-4 py-2 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-xs text-neutral-400">
                    加载中…
                  </td>
                </tr>
              )}
              {!isLoading && users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-xs text-neutral-400">
                    暂无用户
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <UserRow
                  key={u.user_id}
                  user={u}
                  selfId={selfId}
                  onReset={() => setResetting(u)}
                  onDelete={() => setDeleting(u)}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <NewUserModal
        open={creating}
        onOpenChange={setCreating}
        tenants={tenants.filter((t) => t.status === 'active')}
        onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })}
      />
      <ResetPasswordModal
        target={resetting}
        onOpenChange={(b) => !b && setResetting(null)}
        endpoint={(id) => `admin/users/${id}/reset-password`}
      />
      <DeleteUserDialog
        target={deleting}
        onOpenChange={(b) => !b && setDeleting(null)}
        onDeleted={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })}
      />
    </div>
  );
}

function UserRow({
  user,
  selfId,
  onReset,
  onDelete,
  onChanged,
}: {
  user: AdminUser;
  selfId: string | undefined;
  onReset: () => void;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const isRoot = user.role === 'SUPER_ADMIN';
  const isSelf = user.user_id === selfId;
  const toggle = useMutation({
    mutationFn: () =>
      api
        .patch(`admin/users/${user.user_id}`, {
          json: { status: user.status === 'active' ? 'disabled' : 'active' },
        })
        .json<AdminUser>(),
    onSuccess: () => {
      toast.success('用户状态已更新');
      onChanged();
    },
    onError: async (e) => toast.error('更新失败', await extractDetail(e)),
  });
  return (
    <tr className="border-b border-outline-variant/15 last:border-0">
      <td className="px-4 py-2.5 font-mono text-xs text-neutral-700">{user.username}</td>
      <td className="px-4 py-2.5">{user.display_name}</td>
      <td className="px-4 py-2.5">
        <RoleBadge role={user.role} />
      </td>
      <td className="px-4 py-2.5 font-mono text-xs text-neutral-600">{user.tenant_id ?? '—'}</td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${
            user.status === 'active'
              ? 'bg-success/10 text-success'
              : 'bg-neutral-100 text-neutral-500'
          }`}
        >
          {user.status === 'active' ? '启用' : '已停用'}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" disabled={isRoot || isSelf} onClick={onReset}>
            <KeyRound className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isRoot || isSelf}
            title={isRoot ? '不能删除超级管理员' : isSelf ? '不能删除自己' : undefined}
            onClick={onDelete}
          >
            <Trash2 className="h-3.5 w-3.5 text-danger" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isRoot || isSelf || toggle.isPending}
            title={isSelf ? '不能停用/启用自己' : undefined}
            onClick={() => toggle.mutate()}
          >
            {user.status === 'active' ? '停用' : '启用'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const cfg: Record<Role, { label: string; cls: string }> = {
    SUPER_ADMIN: { label: '超管', cls: 'bg-danger/10 text-danger' },
    TENANT_ADMIN: { label: '租管', cls: 'bg-brand-100 text-brand-700' },
    TENANT_MEMBER: { label: '成员', cls: 'bg-neutral-100 text-neutral-700' },
  };
  const c = cfg[role];
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] ${c.cls}`}>{c.label}</span>
  );
}

function NewUserModal({
  open,
  onOpenChange,
  tenants,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  tenants: Tenant[];
  onCreated: () => void;
}) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'TENANT_ADMIN' | 'TENANT_MEMBER'>('TENANT_MEMBER');
  const [tenantId, setTenantId] = useState(tenants[0]?.tenant_id ?? '');
  const create = useMutation({
    mutationFn: () =>
      api
        .post('admin/users', {
          json: {
            username: username.trim(),
            display_name: displayName.trim(),
            password,
            email: email.trim() || undefined,
            role,
            tenant_id: tenantId,
          },
        })
        .json<AdminUser>(),
    onSuccess: () => {
      toast.success('用户已创建');
      onOpenChange(false);
      setUsername('');
      setDisplayName('');
      setPassword('');
      setEmail('');
      setRole('TENANT_MEMBER');
      onCreated();
    },
    onError: async (e) => toast.error('创建失败', await extractDetail(e)),
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="新建用户"
      description="账号 3-32 位字母数字下划线;密码至少 10 位"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
            创建
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="space-y-3"
      >
        <Field label="账号">
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            pattern="^[a-zA-Z0-9_.-]{3,32}$"
            placeholder="3-32 位字母/数字/_.-"
            title="3-32 位,仅支持字母、数字、下划线、点、连字符"
            required
          />
        </Field>
        <Field label="显示名">
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </Field>
        <Field label="密码">
          <PasswordField value={password} onChange={setPassword} />
        </Field>
        <Field label="邮箱(可选)">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="角色">
            <Select
              value={role}
              onChange={(v) => setRole(v as 'TENANT_ADMIN' | 'TENANT_MEMBER')}
              options={[
                { value: 'TENANT_MEMBER', label: '租户成员' },
                { value: 'TENANT_ADMIN', label: '租户管理员' },
              ]}
            />
          </Field>
          <Field label="所属租户">
            <Select
              value={tenantId}
              onChange={setTenantId}
              options={tenants.map((t) => ({ value: t.tenant_id, label: t.name }))}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

export function ResetPasswordModal({
  target,
  onOpenChange,
  endpoint,
}: {
  target: { user_id: string; display_name: string; username: string } | null;
  onOpenChange: (b: boolean) => void;
  endpoint: (id: string) => string;
}) {
  const [pw, setPw] = useState('');
  const reset = useMutation({
    mutationFn: () =>
      target ? api.post(endpoint(target.user_id), { json: { new_password: pw } }).json() : Promise.resolve(),
    onSuccess: () => {
      toast.success('密码已重置');
      setPw('');
      onOpenChange(false);
    },
    onError: async (e) => toast.error('重置失败', await extractDetail(e)),
  });
  return (
    <Modal
      open={!!target}
      onOpenChange={onOpenChange}
      title={target ? `重置 ${target.display_name} 的密码` : '重置密码'}
      description="新密码至少 10 位"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={reset.isPending || pw.length < 10}
            onClick={() => reset.mutate()}
          >
            确认重置
          </Button>
        </>
      }
    >
      <Field label="新密码">
        <PasswordField value={pw} onChange={setPw} autoFocus />
      </Field>
    </Modal>
  );
}

// ---------- shared atoms ----------

function DeleteUserDialog({
  target,
  onOpenChange,
  onDeleted,
}: {
  target: AdminUser | null;
  onOpenChange: (b: boolean) => void;
  onDeleted: () => void;
}) {
  const del = useMutation({
    mutationFn: () =>
      target ? api.delete(`admin/users/${target.user_id}`).then(() => undefined) : Promise.resolve(),
    onSuccess: () => {
      toast.success('用户已删除');
      onOpenChange(false);
      onDeleted();
    },
    onError: async (e) => toast.error('删除失败', await extractDetail(e)),
  });
  return (
    <ConfirmDialog
      open={!!target}
      onOpenChange={onOpenChange}
      title={target ? `删除用户 ${target.display_name}?` : '删除用户'}
      description={
        target
          ? `账号 ${target.username}(${target.tenant_id ?? '无租户'})将被永久删除,此操作不可撤销。`
          : undefined
      }
      confirmText="确认删除"
      tone="danger"
      pending={del.isPending}
      onConfirm={() => del.mutate()}
    />
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-neutral-600">{label}</span>
      {children}
    </label>
  );
}

export function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-10 w-full rounded-lg border border-outline-variant bg-surface px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// re-export icons for nav usage
export { ShieldCheck, UserCog };
